import type { ReactNode } from 'react';

/**
 * Ornate avatar frames (original artwork): a patterned band round the photo with gold edges and, on the richer
 * frames, rosettes in the corners — like the reference's styling frames. Drawn in a 100×100 box over the card;
 * the photo shows through the window from 13 to 87.
 */
type Motif = 'beads' | 'diamonds' | 'flowers' | 'hearts' | 'stars' | 'leaves' | 'checks' | 'chevrons';

interface Look {
  motif: Motif;
  /** Band colour, motif colour ('rainbow' cycles colours), small accent, gold edges. */
  band: string;
  ink: string;
  accent: string;
  edge: string;
  corners?: boolean;
}

const GOLD = '#e8b04a';

const LOOKS: Record<string, Look> = {
  rose_garden: {
    motif: 'flowers',
    band: '#7a1f3d',
    ink: '#ff6f9f',
    accent: '#ffd36b',
    edge: GOLD,
    corners: true,
  },
  neon_pink: { motif: 'diamonds', band: '#3a0a2c', ink: '#ff3db8', accent: '#ffc2ec', edge: '#ff7ad9' },
  neon_cyan: { motif: 'diamonds', band: '#062a33', ink: '#22e3ff', accent: '#bff8ff', edge: '#6ff0ff' },
  hearts: { motif: 'hearts', band: '#ffd6e6', ink: '#ff3d8b', accent: '#ffffff', edge: '#ff7aa8' },
  candy: { motif: 'chevrons', band: '#fff1f7', ink: '#ff5fa2', accent: '#58c7ff', edge: '#ff8ec0' },
  bow: { motif: 'beads', band: '#ffc9de', ink: '#ff4f93', accent: '#ffffff', edge: GOLD, corners: true },
  leaves: {
    motif: 'leaves',
    band: '#3b2a12',
    ink: '#e8a33a',
    accent: '#c4561b',
    edge: '#c99a3b',
    corners: true,
  },
  flame: { motif: 'diamonds', band: '#4a0d00', ink: '#ff7a1a', accent: '#ffd23f', edge: '#ff9d2e' },
  ice: { motif: 'stars', band: '#dff6ff', ink: '#5cc8f0', accent: '#ffffff', edge: '#8edcff', corners: true },
  butterfly: { motif: 'flowers', band: '#efe1ff', ink: '#9b5de5', accent: '#f15bb5', edge: '#b388ff' },
  emerald: { motif: 'checks', band: '#0d4d33', ink: '#2ec27e', accent: '#d9f99d', edge: GOLD },
  ruby: { motif: 'checks', band: '#5a0d16', ink: '#e0313e', accent: '#ffc2c7', edge: GOLD },
  sapphire: { motif: 'checks', band: '#0b2560', ink: '#3d6fe0', accent: '#c7d7ff', edge: GOLD },
  devil: {
    motif: 'chevrons',
    band: '#1a0505',
    ink: '#e01e1e',
    accent: '#ff8a00',
    edge: '#a3150f',
    corners: true,
  },
  angel: {
    motif: 'beads',
    band: '#fffbe8',
    ink: '#f3c74b',
    accent: '#ffffff',
    edge: '#e8c766',
    corners: true,
  },
  galaxy: { motif: 'stars', band: '#1c1442', ink: '#b9a6ff', accent: '#ffe066', edge: '#6c5ce7' },
  rainbow: { motif: 'beads', band: '#ffffff', ink: 'rainbow', accent: '#ffffff', edge: '#ffb84d' },
  royal: {
    motif: 'diamonds',
    band: '#4a1470',
    ink: '#f2c94c',
    accent: '#ffffff',
    edge: '#f2c94c',
    corners: true,
  },
  vip_gold: {
    motif: 'beads',
    band: '#7a5410',
    ink: '#ffd34d',
    accent: '#fff3c4',
    edge: '#ffd34d',
    corners: true,
  },
  vip_diamond: {
    motif: 'diamonds',
    band: '#e9f6ff',
    ink: '#7fd3ff',
    accent: '#ffffff',
    edge: '#b8e7ff',
    corners: true,
  },
  league_champion: {
    motif: 'stars',
    band: '#5a3a00',
    ink: '#ffd34d',
    accent: '#ffffff',
    edge: '#ffd34d',
    corners: true,
  },
  kiss_master: {
    motif: 'hearts',
    band: '#5a0c1e',
    ink: '#ff4d6d',
    accent: '#ffb3c1',
    edge: '#ff8fa3',
    corners: true,
  },
};

const RAINBOW = ['#ff4d4d', '#ffa53d', '#ffe14d', '#4dd97a', '#4db8ff', '#9b6bff'];
/** The band's centre line (the band runs from the edge to 13). */
const MID = 6.5;
const HEART =
  'M0 2.6C-1.6 1.4-3 .4-3-.9-3-1.8-2.3-2.5-1.5-2.5c.7 0 1.2.4 1.5.9.3-.5.8-.9 1.5-.9.8 0 1.5.7 1.5 1.6 0 1.3-1.4 2.3-3 3.5Z';
const STAR = Array.from({ length: 10 }, (_, i) => {
  const r = i % 2 ? 1.3 : 3.2;
  const a = (Math.PI / 5) * i - Math.PI / 2;
  return `${(r * Math.cos(a)).toFixed(2)},${(r * Math.sin(a)).toFixed(2)}`;
}).join(' ');

/** Spots along the band, side by side round the square, each turned to follow its side. */
function spots(step: number, corners: boolean): { x: number; y: number; turn: number }[] {
  const from = corners ? MID + 11 : MID;
  const to = 100 - from;
  const n = Math.max(1, Math.round((to - from) / step));
  const d = (to - from) / n;
  const out: { x: number; y: number; turn: number }[] = [];
  // Without rosettes each side starts at its corner and stops one step short of the next.
  for (let i = 0; i < (corners ? n + 1 : n); i++) {
    const t = from + i * d;
    out.push(
      { x: t, y: MID, turn: 0 },
      { x: 100 - MID, y: t, turn: 90 },
      { x: 100 - t, y: 100 - MID, turn: 180 },
      { x: MID, y: 100 - t, turn: 270 },
    );
  }
  return out;
}

function motif(look: Look, ink: string, i: number): ReactNode {
  switch (look.motif) {
    case 'beads':
      return (
        <>
          <circle r="2.7" fill={ink} />
          <circle cx="-0.9" cy="-0.9" r="0.9" fill={look.accent} opacity="0.9" />
        </>
      );
    case 'diamonds':
      return (
        <>
          <path d="M0-3.4 3.4 0 0 3.4-3.4 0Z" fill={i % 2 ? look.accent : ink} />
          <path d="M0-1.4 1.4 0 0 1.4-1.4 0Z" fill={i % 2 ? ink : look.accent} />
        </>
      );
    case 'flowers':
      return (
        <>
          {[0, 72, 144, 216, 288].map((a) => (
            <circle
              key={a}
              cx={(1.9 * Math.cos((a * Math.PI) / 180)).toFixed(2)}
              cy={(1.9 * Math.sin((a * Math.PI) / 180)).toFixed(2)}
              r="1.5"
              fill={ink}
            />
          ))}
          <circle r="1.1" fill={look.accent} />
        </>
      );
    case 'hearts':
      return <path d={HEART} fill={i % 3 === 2 ? look.accent : ink} />;
    case 'stars':
      return <polygon points={STAR} fill={i % 2 ? look.accent : ink} />;
    case 'leaves':
      return (
        <>
          <ellipse rx="3" ry="1.25" transform="rotate(45)" fill={ink} />
          <ellipse rx="3" ry="1.25" transform="rotate(-45)" fill={look.accent} />
        </>
      );
    case 'checks':
      return (
        <>
          <rect x="-2.4" y="-2.4" width="2.4" height="2.4" fill={ink} />
          <rect x="0" y="-2.4" width="2.4" height="2.4" fill={look.accent} />
          <rect x="-2.4" y="0" width="2.4" height="2.4" fill={look.accent} />
          <rect x="0" y="0" width="2.4" height="2.4" fill={ink} />
        </>
      );
    case 'chevrons':
      return (
        <>
          <path
            d="M-2.6 1.6 0-1.6 2.6 1.6"
            fill="none"
            stroke={ink}
            strokeWidth="1.7"
            strokeLinejoin="round"
          />
          <circle cy="2.4" r="0.7" fill={look.accent} />
        </>
      );
  }
}

function Rosette({ x, y, look }: { x: number; y: number; look: Look }) {
  const ink = look.ink === 'rainbow' ? RAINBOW[0]! : look.ink;
  return (
    <g transform={`translate(${x} ${y})`}>
      <circle r="7.4" fill={look.edge} />
      <circle r="5.6" fill={ink} />
      {Array.from({ length: 8 }, (_, k) => (
        <circle
          key={k}
          cx={(4.2 * Math.cos((k * Math.PI) / 4)).toFixed(2)}
          cy={(4.2 * Math.sin((k * Math.PI) / 4)).toFixed(2)}
          r="1.1"
          fill={look.accent}
        />
      ))}
      <circle r="2" fill={look.edge} />
    </g>
  );
}

/** The main colour of a frame's pattern, for its symbol in the styling grid. */
export function frameInk(code: string): string | undefined {
  const ink = LOOKS[code]?.ink;
  return ink === 'rainbow' ? RAINBOW[0] : ink;
}

/** True when a frame has an ornate drawing (otherwise the table falls back to its colour ring). */
export function hasFrameArt(code: string | null | undefined): boolean {
  return Boolean(code && LOOKS[code]);
}

/** The frame drawn over a square card (position it with the .frame-art class). */
export function FrameArt({ code }: { code: string }) {
  const look = LOOKS[code];
  if (!look) return null;
  const step = look.motif === 'checks' ? 4.8 : 8.6;
  return (
    <svg className="frame-art" viewBox="0 0 100 100" aria-hidden>
      <path
        d="M3 0H97A3 3 0 0 1 100 3V97A3 3 0 0 1 97 100H3A3 3 0 0 1 0 97V3A3 3 0 0 1 3 0ZM13 13V87H87V13Z"
        fill={look.band}
        fillRule="evenodd"
      />
      {spots(step, Boolean(look.corners)).map((p, i) => (
        <g key={i} transform={`translate(${p.x.toFixed(2)} ${p.y.toFixed(2)}) rotate(${p.turn})`}>
          {motif(look, look.ink === 'rainbow' ? RAINBOW[i % RAINBOW.length]! : look.ink, i)}
        </g>
      ))}
      <rect
        x="0.9"
        y="0.9"
        width="98.2"
        height="98.2"
        rx="3"
        fill="none"
        stroke={look.edge}
        strokeWidth="1.8"
      />
      <rect x="12.4" y="12.4" width="75.2" height="75.2" fill="none" stroke={look.edge} strokeWidth="1.4" />
      {look.corners &&
        [
          [MID, MID],
          [100 - MID, MID],
          [100 - MID, 100 - MID],
          [MID, 100 - MID],
        ].map(([x, y]) => <Rosette key={`${x}-${y}`} x={x!} y={y!} look={look} />)}
    </svg>
  );
}
